/**
 * ScormLibraryPage — Course Packages library under Content.
 * Trainers/editors upload, browse, test-launch (preview, no tracking) and
 * delete SCORM 1.2 / xAPI packages before assigning them to batches.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Package, UploadCloud, Trash2, PlayCircle, FileArchive, Users, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';

const fmtBytes = (n) => {
  if (!n) return '0 B';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ScormLibraryPage() {
  const { get, post, del } = useApi();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('content.assign');

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null); // package being test-launched

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await get('/scorm/packages');
      setPackages(r.packages || []);
    } catch { toast.error('Failed to load packages'); }
    finally { setLoading(false); }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const pkg = await post('/scorm/packages/upload', fd, { timeout: 300000 });
      setPackages(list => [{ ...pkg, used_by: 0 }, ...list]);
      toast.success(`${pkg.package_type === 'xapi' ? 'xAPI' : 'SCORM 1.2'} package uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete "${pkg.title}"? This removes all its files.`)) return;
    try {
      await del(`/scorm/packages/${pkg.package_id}`);
      setPackages(list => list.filter(p => p.package_id !== pkg.package_id));
      toast.success('Package deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = packages.filter(p =>
    !search || (p.title || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="scorm-library-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Package className="w-6 h-6" /> Course Packages
          </h1>
          <p className="text-sm text-muted-foreground">
            Upload SCORM 1.2 / xAPI courses, test-launch them, then assign from Trainees → Learning.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Input placeholder="Search packages…" value={search}
                 onChange={e => setSearch(e.target.value)}
                 className="w-56" data-testid="scorm-lib-search" />
          {canManage && (
            <label>
              <input type="file" accept=".zip" className="hidden"
                     onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }}
                     data-testid="scorm-lib-upload-input" />
              <Button asChild disabled={uploading}>
                <span className="cursor-pointer" data-testid="scorm-lib-upload-btn">
                  <UploadCloud className="w-4 h-4 mr-1.5" />
                  {uploading ? 'Uploading…' : 'Upload package'}
                </span>
              </Button>
            </label>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground p-8 text-center">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="scorm-lib-empty">
          <FileArchive className="w-10 h-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="font-medium">No course packages yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Upload a SCORM 1.2 or xAPI (TinCan) .zip exported from Articulate, Captivate, iSpring, etc.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="scorm-lib-grid">
          {filtered.map(pkg => (
            <Card key={pkg.package_id} className="p-4 flex flex-col gap-3"
                  data-testid={`scorm-pkg-${pkg.package_id}`}>
              <div className="flex items-start gap-2">
                <Package className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate" title={pkg.title}>{pkg.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{pkg.original_filename}</p>
                </div>
                <Badge variant="outline" className="text-[10px] uppercase shrink-0">
                  {pkg.package_type === 'xapi' ? 'xAPI' : 'SCORM 1.2'}
                </Badge>
              </div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>{fmtBytes(pkg.total_bytes)}</span>
                <span>· {pkg.file_count} files</span>
                <span>· {new Date(pkg.created_at).toLocaleDateString()}</span>
                <span className="ml-auto inline-flex items-center gap-1" title="Assignments using this package">
                  <Users className="w-3 h-3" /> {pkg.used_by || 0}
                </span>
              </div>
              <div className="flex gap-2 mt-auto">
                <Button size="sm" variant="outline" className="flex-1"
                        onClick={() => setPreview(pkg)}
                        data-testid={`scorm-preview-btn-${pkg.package_id}`}>
                  <PlayCircle className="w-3.5 h-3.5 mr-1" /> Test launch
                </Button>
                {canManage && (
                  <Button size="sm" variant="ghost"
                          onClick={() => remove(pkg)}
                          disabled={(pkg.used_by || 0) > 0}
                          title={(pkg.used_by || 0) > 0 ? 'In use by an assignment' : 'Delete package'}
                          data-testid={`scorm-delete-btn-${pkg.package_id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {preview && <PreviewDialog pkg={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

/** Test-launch dialog — runs the course with a throwaway in-memory SCORM API
 *  (nothing persisted) or a preview xAPI token (statements never touch
 *  trainee progress). */
function PreviewDialog({ pkg, onClose }) {
  const { get } = useApi();
  const [launch, setLaunch] = useState(null);
  const [error, setError] = useState(null);
  const [apiReady, setApiReady] = useState(false);
  const cmiRef = useRef({});

  useEffect(() => {
    get(`/scorm/packages/${pkg.package_id}/preview-launch`)
      .then(setLaunch)
      .catch(e => setError(e.response?.data?.detail || 'Failed to launch preview'));
  }, [get, pkg.package_id]);

  useEffect(() => {
    if (!launch || launch.package_type !== 'scorm12') return undefined;
    cmiRef.current = {
      'cmi.core.student_id': 'preview',
      'cmi.core.student_name': 'Preview User',
      'cmi.core.lesson_status': 'not attempted',
      'cmi.core.lesson_mode': 'browse',
      'cmi.core.entry': 'ab-initio',
      'cmi.core.credit': 'no-credit',
      'cmi.suspend_data': '',
      'cmi.core.score.raw': '',
    };
    window.API = {
      LMSInitialize: () => 'true',
      LMSFinish: () => 'true',
      LMSGetValue: (k) => (cmiRef.current[k] !== undefined ? String(cmiRef.current[k]) : ''),
      LMSSetValue: (k, v) => { cmiRef.current[k] = String(v); return 'true'; },
      LMSCommit: () => 'true',
      LMSGetLastError: () => '0',
      LMSGetErrorString: () => 'No error',
      LMSGetDiagnostic: () => '',
    };
    setApiReady(true);
    return () => { delete window.API; setApiReady(false); };
  }, [launch]);

  let src = launch?.content_url;
  if (launch?.package_type === 'xapi') {
    const qs = new URLSearchParams({
      endpoint: `${window.location.origin}/api/v1/xapi`,
      auth: `Basic ${btoa(`${launch.launch_token}:`)}`,
      actor: JSON.stringify(launch.actor),
      activity_id: launch.activity_id,
      registration: launch.registration,
    });
    src = `${launch.content_url}${launch.content_url.includes('?') ? '&' : '?'}${qs}`;
  }
  const ready = launch && (launch.package_type === 'xapi' || apiReady);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="scorm-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlayCircle className="w-5 h-5" /> {pkg.title}
            <Badge variant="outline" className="text-[10px] uppercase">
              {pkg.package_type === 'xapi' ? 'xAPI' : 'SCORM 1.2'}
            </Badge>
            <Badge className="text-[10px] bg-amber-100 text-amber-800 hover:bg-amber-100">
              Preview — progress not recorded
            </Badge>
          </DialogTitle>
        </DialogHeader>
        {error ? (
          <p className="p-8 text-center text-sm text-rose-600" data-testid="scorm-preview-error">{error}</p>
        ) : !ready ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Preparing preview…</p>
        ) : (
          <iframe
            title={`Preview — ${pkg.title}`}
            src={src}
            className="w-full h-[65vh] rounded-lg border bg-white"
            allowFullScreen
            data-testid="scorm-preview-iframe"
          />
        )}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="scorm-preview-close-btn">
            <X className="w-3.5 h-3.5 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
